import { useEffect, useMemo, useState } from "react";
import { message } from "antd";

import { AppStrings } from "../../../utils/constants/AppStrings";
import { useCurrencySymbol } from "../../../hooks/useCurrencySymbol";
import {
  buildProfitabilityDetailsParams,
  mapTopEventsRows,
  useProfitabilityTopEvents,
  useProfitabilityTopEventsExport,
  type ProfitabilityMetricScope,
} from "../../../queries/sportsbook/profitabilityDetails";
import type { ReportingSummaryFiltersValue } from "../../../queries/sportsbook/reportingSummaryFilterOptions";
import { triggerBrowserDownload } from "../../../utils/triggerBrowserDownload";
import { getProfitabilityTopEventsColumns } from "../profitabilityReportingColumns";
import ProfitabilityTableSection from "./ProfitabilityTableSection";
import {
  useProfitabilityFilterOptions,
  useProfitabilitySectionFilterState,
} from "./useProfitabilitySectionFilters";

type Props = {
  date: string;
  inheritedFilters: ReportingSummaryFiltersValue;
};

function ProfitabilityTopEventsSection({ date, inheritedFilters }: Props) {
  const currencySymbol = useCurrencySymbol() ?? "$";
  const [metricScope, setMetricScope] =
    useState<ProfitabilityMetricScope>("total");
  const { filters, setFilters, setBetType, setMoneyType } =
    useProfitabilitySectionFilterState(inheritedFilters);
  const params = useMemo(() => ({ date, ...filters }), [date, filters]);
  const query = useProfitabilityTopEvents(params);
  const options = useProfitabilityFilterOptions(
    query.data?.filters ?? [],
    setFilters,
    date,
  );
  const rows = useMemo(
    () => mapTopEventsRows(query.data?.rows ?? [], metricScope),
    [query.data?.rows, metricScope],
  );
  const exportMutation = useProfitabilityTopEventsExport();
  const columns = useMemo(
    () => getProfitabilityTopEventsColumns(currencySymbol),
    [currencySymbol],
  );

  useEffect(() => {
    if (query.isError) {
      message.error(AppStrings.REPORTING_SUMMARY_FETCH_ERROR);
    }
  }, [query.isError]);

  const exportState = {
    isBusy: exportMutation.isPending,
    start: (payload: Record<string, unknown>) =>
      exportMutation.mutate(payload, {
        onSuccess: (download) => {
          if (download?.download_url) {
            triggerBrowserDownload(download.download_url);
            return;
          }
          message.error(AppStrings.REPORTING_SUMMARY_EXPORT_ERROR);
        },
        onError: () =>
          message.error(AppStrings.REPORTING_SUMMARY_EXPORT_ERROR),
      }),
  };

  return (
    <ProfitabilityTableSection
      id="reporting-profitability-top-events"
      title={AppStrings.SPORTSBOOK_PROFITABILITY_TOP_EVENTS}
      rows={rows}
      columns={columns}
      paginated
      showRankingFilter
      loading={query.isLoading || query.isFetching}
      metricScope={metricScope}
      onMetricScopeChange={setMetricScope}
      betType={filters.bet_type}
      moneyType={filters.money_type}
      betTypeOptions={options?.bet_type}
      moneyTypeOptions={options?.money_type}
      onBetTypeChange={setBetType}
      onMoneyTypeChange={setMoneyType}
      exportState={exportState}
      buildExportPayload={() => buildProfitabilityDetailsParams(params)}
    />
  );
}

export default ProfitabilityTopEventsSection;
